/**
 * PIPELINE MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Pattern di riferimento: `quotationMappers.ts`. A differenza del preventivo la
 * pipeline non ha campi enum (niente tabelle int<->string): i mapper si riducono
 * a passthrough dei campi più la conversione dell'array annidato degli stati.
 *
 * Fonte di verità dei types Pipeline: `Pipeline`, `PipelineState`,
 * `PipelineDto`, `PipelineStateDto`, `PipelineInput`, `PipelineStateInput`
 * vivono qui; `pipelineTypes.ts` li re-esporta e aggiunge i tipi UI-centrici
 * (stato dello slice, payload create/update, list params).
 *
 * ## Salvataggio aggregato
 *
 * La pipeline e i suoi stati si salvano insieme: `PipelineInput.states` porta
 * l'elenco COMPLETO, e il backend riconcilia (crea i nuovi con
 * `pipelineStateId` assente/0, aggiorna i presenti, cancella i mancanti,
 * riassegna la posizione dall'ordine dell'array). La `position` non è in input:
 * è derivata dall'ordine.
 */

import { enumFromDto, pickAuditFields, type AuditFields } from '@crm/shared';

// ─── kind: caratteristica dello stato (SYNC CROSS-LAYER) ───────────────
//
// Mappato 1:1 con it.dvel.crm.util.constants.PipelineStateKind (Java). Se
// aggiungi/rimuovi/rinomini un valore, aggiornare IN PARALLELO:
//   - modules/crm-util/.../constants/PipelineStateKind.java (enum + getValue)
//   - PipelineForm.tsx (controlli iniziale/vinto/perso per riga)
//   - locales/it.json e en.json (chiavi pipeline.form.initial/finalWon/finalLost)
export type PipelineStateKind = 'normal' | 'initial' | 'won' | 'lost';

/** UI (string) -> wire (int) dell'enum PipelineStateKind. */
export const KIND_TO_INT: Record<PipelineStateKind, number> = {
  normal: 0,
  initial: 1,
  won: 2,
  lost: 3,
};

export const KIND_FROM_INT: Record<number, PipelineStateKind> = {
  0: 'normal',
  1: 'initial',
  2: 'won',
  3: 'lost',
};

// ─── DTO REST (shape wire) ─────────────────────────────────────────────

export interface PipelineStateDto {
  pipelineStateId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  pipelineId: number;
  code: string;
  description: string | null;
  /** Caratteristica (int enum PipelineStateKind): 0=normale, 1=iniziale, 2=vinto, 3=perso. */
  kind: number;
  position: number;
}

export interface PipelineDto {
  pipelineId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  name: string;
  description: string | null;
  /** FQN Java del tipo di entita' del funnel (TS-117). Sul wire viaggia l'FQN, non il nome corto. */
  className: string;
  states: PipelineStateDto[];
}

// ─── Shape UI ──────────────────────────────────────────────────────────

export interface PipelineState extends AuditFields {
  pipelineStateId: number;
  pipelineId: number;
  code: string;
  description: string | null;
  kind: PipelineStateKind;
  position: number;
}

export interface Pipeline extends AuditFields {
  pipelineId: number;
  name: string;
  description: string | null;
  /**
   * FQN Java del tipo di entita' le cui schede vivono in questo funnel (TS-117).
   *
   * Si tiene l'FQN e non il `typeName` corto perche' e' la stessa stringa che il backend usa, ed e'
   * quella su cui `getEntityShortNameByFqn` risolve il descrittore. Convertire al boundary lascerebbe
   * due forme dello stesso dato in giro per il client.
   */
  className: string;
  states: PipelineState[];
}

// ─── DTO di input (POST /pipelines/{groupId} e PATCH /pipeline/{id}) ────

/**
 * Stato in input al salvataggio aggregato. `pipelineStateId` assente o 0
 * identifica uno stato NUOVO; valorizzato uno esistente da aggiornare.
 */
export interface PipelineStateInput {
  pipelineStateId?: number;
  code: string;
  description?: string | null;
  /** Caratteristica come int (enum PipelineStateKind). Vedi KIND_TO_INT. */
  kind?: number;
}

export interface PipelineInput {
  name?: string;
  description?: string | null;
  /**
   * FQN del tipo di entita'. Obbligatorio in CREAZIONE e assente in aggiornamento: il tipo non e'
   * modificabile dopo, perche' cambiarlo lascerebbe record di un tipo dentro stati di un altro.
   */
  className?: string;
  states?: PipelineStateInput[];
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

function pipelineStateFromDto(dto: PipelineStateDto): PipelineState {
  return {
    pipelineStateId: dto.pipelineStateId,
    ...pickAuditFields(dto),
    pipelineId: dto.pipelineId,
    code: dto.code,
    description: dto.description,
    kind: enumFromDto('PipelineStateKind', dto.kind, KIND_FROM_INT, 'normal'),
    position: dto.position,
  };
}

/** DTO REST -> modello UI. Usato su GET e sulle response di POST/PATCH. */
export function pipelineFromDto(dto: PipelineDto): Pipeline {
  return {
    pipelineId: dto.pipelineId,
    ...pickAuditFields(dto),
    name: dto.name,
    description: dto.description,
    className: dto.className,
    states: (dto.states ?? []).map(pipelineStateFromDto),
  };
}

function pipelineStateToDto(s: PipelineState): PipelineStateDto {
  return {
    pipelineStateId: s.pipelineStateId,
    ...pickAuditFields(s),
    pipelineId: s.pipelineId,
    code: s.code,
    description: s.description,
    kind: KIND_TO_INT[s.kind],
    position: s.position,
  };
}

/**
 * Modello UI -> DTO REST. Serve ai mock MSW per riprodurre la shape emessa dal
 * backend. L'app reale non lo chiama: chi salva passa da
 * `pipelineCreateToInput` / `pipelineUpdateToInput`.
 */
export function pipelineToDto(p: Pipeline): PipelineDto {
  return {
    pipelineId: p.pipelineId,
    ...pickAuditFields(p),
    name: p.name,
    description: p.description,
    className: p.className,
    states: p.states.map(pipelineStateToDto),
  };
}

/**
 * Payload create UI -> DTO input. L'intero array di stati viaggia com'è: il
 * backend ne fa la riconciliazione.
 */
export function pipelineCreateToInput(p: {
  name: string;
  description?: string | null;
  className: string;
  states: PipelineStateInput[];
}): PipelineInput {
  return {
    name: p.name,
    description: p.description ?? null,
    className: p.className,
    states: p.states,
  };
}

/**
 * Payload update UI -> DTO input parziale (PATCH). `name`/`description` assenti
 * mantengono il valore corrente; `states`, se presente, sostituisce l'insieme.
 */
export function pipelineUpdateToInput(p: {
  name?: string;
  description?: string | null;
  states?: PipelineStateInput[];
}): PipelineInput {
  const out: PipelineInput = {};
  if (p.name !== undefined) out.name = p.name;
  if (p.description !== undefined) out.description = p.description;
  if (p.states !== undefined) out.states = p.states;
  return out;
}
